import { Building2, CheckCircle2, Search } from "lucide-react";
import { trpc } from "../trpc";
import { useState, useEffect } from "react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { Spinner } from "@/components/ui/spinner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";

type Organization = {
  id: string;
  name: string;
  slug: string;
};

type OrganizationSwitcherProps = {
  isOpen: boolean;
  onClose: () => void;
  currentOrganizationId?: string;
};

const OrganizationSwitcher = ({
  isOpen,
  onClose,
  currentOrganizationId,
}: OrganizationSwitcherProps): JSX.Element => {
  const [searchTerm, setSearchTerm] = useState("");

  const { data, isLoading, error } = trpc.auth.getMemberOrganizations.useQuery(
    undefined,
    {
      enabled: isOpen,
    }
  );

  // Clear search when modal closes
  useEffect(() => {
    if (!isOpen) {
      setSearchTerm("");
    }
  }, [isOpen]);

  const switchOrgMutation = trpc.auth.switchOrganization.useMutation({
    onSuccess: (data) => {
      toast.success("Organización cambiada", {
        description: `Ahora estás en ${data.organization.name}`,
      });

      // Update the session token in cookies
      const maxAge = 60 * 60 * 24 * 180; // 180 days in seconds
      document.cookie = `stytch_session=${data.sessionToken}; path=/; max-age=${maxAge}; samesite=lax`;
      document.cookie = `stytch_session_jwt=${data.sessionJwt}; path=/; max-age=${maxAge}; samesite=lax`;
      localStorage.setItem('stytch_session_token', data.sessionToken);

      onClose();

      // Reload page to refresh all data with new organization context
      setTimeout(() => {
        window.location.reload();
      }, 500);
    },
    onError: (error) => {
      toast.error("Error", {
        description: error.message || "Error al cambiar de organización.",
      });
    },
  });

  const handleSwitchOrganization = (org: Organization) => {
    if (org.id === currentOrganizationId) {
      toast.info("Ya estás en esta organización");
      return;
    }

    // Directly switch organizations using session exchange
    switchOrgMutation.mutate({ targetOrganizationId: org.id });
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="flex h-full max-h-full flex-col overflow-hidden md:h-auto md:max-h-[85vh] md:max-w-md">
        <DialogHeader>
          <DialogTitle>Cambiar Organización</DialogTitle>
        </DialogHeader>
        {!isLoading && !error && data?.organizations && data.organizations.length > 1 && (
          <div className="flex flex-col gap-3">
            <p className="text-sm text-gray-600">
              Selecciona una organización:
            </p>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-gray-400" />
              <Input
                placeholder="Buscar organización..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                autoFocus
                className="pl-10"
              />
            </div>
          </div>
        )}
        <div className="min-h-0 flex-1 overflow-y-auto pb-6">
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <Spinner size="lg" />
            </div>
          ) : error ? (
            <div className="py-4">
              <p className="text-red-500">Error al cargar organizaciones</p>
            </div>
          ) : (
            <div className="flex flex-col gap-3">
              {data?.organizations && data.organizations.length > 1 ? (
                <>
                  {(() => {
                    const filteredOrgs = data.organizations
                      .filter((org): org is Organization =>
                        Boolean(org?.id && org?.name && org?.slug)
                      )
                      .filter((org) =>
                        org.name.toLowerCase().includes(searchTerm.toLowerCase())
                      );

                    if (filteredOrgs.length === 0) {
                      return (
                        <div className="py-8 text-center">
                          <p className="text-gray-500">
                            No se encontraron organizaciones
                          </p>
                        </div>
                      );
                    }

                    return filteredOrgs.map((org) => {
                      const isCurrent = org.id === currentOrganizationId;
                      return (
                        <div
                          key={org.id}
                          className={cn(
                            "cursor-pointer rounded-md border p-4 transition-colors",
                            isCurrent
                              ? "border-purple-500 bg-purple-50 cursor-default"
                              : "border-gray-200 bg-white hover:border-purple-300 hover:bg-gray-50",
                            switchOrgMutation.isLoading && "cursor-not-allowed opacity-60"
                          )}
                          onClick={() =>
                            !isCurrent && !switchOrgMutation.isLoading && handleSwitchOrganization(org)
                          }
                        >
                          <div className="flex items-center gap-3">
                            <Building2
                              className={cn(
                                "size-5",
                                isCurrent ? "text-purple-600" : "text-gray-600"
                              )}
                            />
                            <div className="flex-1">
                              <p
                                className={cn(
                                  isCurrent ? "font-bold text-purple-700" : "font-medium text-gray-800"
                                )}
                              >
                                {org.name}
                              </p>
                              <p className="text-xs text-gray-500">
                                {org.slug}
                              </p>
                            </div>
                            {isCurrent && (
                              <CheckCircle2 className="size-6 text-purple-500" />
                            )}
                          </div>
                        </div>
                      );
                    });
                  })()}
                  <p className="mt-4 text-xs text-gray-500">
                    {switchOrgMutation.isLoading
                      ? "Cambiando organización..."
                      : "Haz clic en una organización para cambiar instantáneamente."}
                  </p>
                </>
              ) : (
                <div className="py-4 text-center">
                  <p className="text-gray-600">
                    Solo perteneces a una organización.
                  </p>
                </div>
              )}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default OrganizationSwitcher;
